import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { TranslateService } from '@ngx-translate/core';
import { GraphsService } from '@service/graphs/graphs.service';
import { Graph, GraphType, GraphTypeTranslate } from 'icreate/common/models/graphs';
import { Vessel } from 'icreate/common/models/vessel';
import _ from 'lodash';
import { lastValueFrom, Subject, take } from 'rxjs';
import { NewGraphDialogComponent } from '../new-graph-dialog/new-graph-dialog.component';

@Component({
    selector: 'app-edit-graph-dialog',
    templateUrl: './edit-graph-dialog.component.html',
    styleUrl: './edit-graph-dialog.component.css',
    standalone: false,
})
export class EditGraphDialogComponent {
    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;
    @ViewChild('saveWithoutExtra') saveWithoutExtra: ConfirmationWindowComponent;

    closeGraph$: Subject<void> = new Subject<void>();
    saveGraph$: Subject<void> = new Subject<void>();

    graphTypeMap: Map<GraphType, string> = new Map(
        GraphTypeTranslate.map(item => [item.type, this.translateService.instant(item.translate)])
    );

    title: string;
    selectedType: GraphType;
    vessel: Vessel;

    isSubmiting: boolean = false;

    hasError: boolean;
    selectedGraph: Graph;
    saveIsEnabled: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            graph: Graph;
            isEditing: boolean;
        },
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialogRef<EditGraphDialogComponent>,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private graphService: GraphsService
    ) {}

    ngOnInit(): void {
        if (this.data.vessel) {
            this.vessel = this.data.vessel;
        }

        if (this.data.graph) {
            this.selectedGraph = { ...this.data.graph };
        }
    }

    async save() {
        try {
            if (this.data.isEditing || this.isTitleChanged) {
                await this.graphService.updateGraph(this.selectedGraph.title, this.selectedGraph.id);
            }
            this.data.graph = this.selectedGraph;
            this.saveGraph$.next();
            this.dialogRef.close(this.selectedGraph);
        } catch (err) {
            console.error('Save error:', err);
            this.dialogRef.close();
        }
    }

    async renameGraph() {
        const dialogRef = this.dialog.open(NewGraphDialogComponent, {
            disableClose: true,
            width: '474px',
            height: '284px',
            data: {
                vessel: this.vessel,
                graph: this.selectedGraph,
                selectedType: this.selectedGraph.type,
            },
        });
        const newTitle = await lastValueFrom(dialogRef.afterClosed());

        if (newTitle) {
            this.selectedGraph = {
                ...this.selectedGraph,
                title: newTitle,
            };
        }
    }

    async cancel() {
        if (this.saveIsEnabled || this.isTitleChanged) {
            this.cancelConfirmation.setVisible(true);
            let confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                await this.save();
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.DISCARD) {
                this.dialogRef.close();
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                this.cancelConfirmation.setVisible(false);
                return;
            }
        } else if (!(this.saveIsEnabled || this.isTitleChanged) && !this.data.isEditing) {
            this.saveWithoutExtra.setVisible(true);
            let confirmationOption = await lastValueFrom(this.saveWithoutExtra.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                this.dialogRef.close();
            } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                this.saveWithoutExtra.setVisible(false);
                return;
            }
        } else {
            this.dialogRef.close();
        }
    }

    get isExhaustGraph() {
        return this.selectedGraph?.type === GraphType.Exhaust;
    }

    get isBarGraph() {
        return this.selectedGraph?.type === GraphType.Bar;
    }

    get isGaugeGraph() {
        return this.selectedGraph?.type === GraphType.Gauge;
    }

    get isTitleChanged() {
        return !_.isEqual(this.data.graph.title, this.selectedGraph.title);
    }

    getGraphTypeName = (graphType: GraphType) => {
        return this.graphTypeMap.get(graphType) || '';
    };

    updateSaveButton(enableButton) {
        let fireDetect = false;
        if (this.saveIsEnabled !== enableButton) fireDetect = true;
        this.saveIsEnabled = enableButton;
        if (fireDetect) {
            this.cdr.detectChanges();
        }
    }
}
